import React, { useEffect, useRef, useState } from 'react';
import { extractMenuDraft, importReviewedMenu } from '../../services/supabase/menuImportService';
import { menuImportKey, validateReviewedMenuImport } from '../../services/supabase/edge-functions/_shared/menuImport';
import { readMenuImportDraft, menuImportReviewIssues } from '../../services/local/menuImportDraft';
import MenuImportReview, { MenuImportRow } from './MenuImportReview';
import '../../styles/MenuImportPanel.css';

export default function MenuImportPanel(props) {
  return <MenuImportPanelState key={`${props.userId}:${props.menuId}`} {...props} />;
}

function MenuImportPanelState({ menuId, userId, companyName, branchName, shared, categories = [], subcategories = {}, products = [], disabled, onImported, initiallyExpanded = false, guided = false }) {
  const storageKey = `mozzoapp:menu-import:${userId}:${menuId}`;
  const draftKey = `${storageKey}:draft`;
  const [restoredDraft] = useState(() => readMenuImportDraft(draftKey));
  const [pending, setPending] = useState(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(storageKey) || 'null');
      return saved?.requestId && Array.isArray(saved.items) ? saved : null;
    } catch { return null; }
  });
  const [expanded, setExpanded] = useState(Boolean(pending || restoredDraft || initiallyExpanded));
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState(restoredDraft?.rows || []);
  const [warnings, setWarnings] = useState(restoredDraft?.warnings || []);
  const [reviewIndex, setReviewIndex] = useState(restoredDraft?.reviewIndex || 0);
  const [reviewedIds, setReviewedIds] = useState(restoredDraft?.reviewedIds || []);
  const [replaceConfirmed, setReplaceConfirmed] = useState(false);
  const [draftStorageError, setDraftStorageError] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const alive = useRef(true), request = useRef(null), saving = useRef(false);
  useEffect(() => { alive.current = true; return () => { alive.current = false; request.current?.abort(); }; }, []);
  useEffect(() => {
    try {
      if (rows.length) localStorage.setItem(draftKey, JSON.stringify({ version: 2, savedAt: Date.now(), rows, warnings, reviewIndex, reviewedIds }));
      else localStorage.removeItem(draftKey);
      setDraftStorageError(false);
    } catch { setDraftStorageError(true); }
  }, [draftKey, rows, warnings, reviewIndex, reviewedIds]);
  const summary = guided && reviewIndex >= rows.length;
  const allReviewed = rows.length > 0 && rows.every((row) => reviewedIds.includes(row.rowId));
  const canConfirm = !guided || (summary && allReviewed && menuImportReviewIssues(rows).length === 0);
  const duplicate = (row) => {
    const category = categories.find((c) => menuImportKey(c.nombre) === menuImportKey(row.category));
    const subcategory = (subcategories[category?.id] || []).find((s) => menuImportKey(s.nombre) === menuImportKey(row.subcategory));
    return products.some((p) => p.categoria === category?.id && menuImportKey(p.nombre) === menuImportKey(row.name)
      && (row.subcategory ? subcategory && p.subcategoriaId === subcategory.id : !p.subcategoriaId));
  };
  const update = (id, patch) => {
    setRows((old) => old.map((r) => r.rowId === id ? { ...r, ...patch } : r)); setReviewed(false); setError('');
    setReviewedIds((old) => old.filter((rowId) => rowId !== id));
  };
  const analyze = async () => {
    if (!file || busy || disabled || pending || (rows.length > 0 && !replaceConfirmed)) return;
    setBusy('reading'); setError(''); setResult(null);
    const controller = new AbortController(); request.current = controller;
    const timeout = setTimeout(() => controller.abort(), 80000);
    try {
      const draft = await extractMenuDraft(menuId, file, { signal: controller.signal });
      if (!alive.current || controller.signal.aborted) return;
      if (!draft.items?.length) throw new Error('No se identificaron platos. El borrador anterior se conserva. Proba con otro archivo.');
      setWarnings(draft.warnings || []);
      setRows(draft.items.map((row) => ({ ...row, selected: !duplicate(row) })));
      setReviewIndex(0); setReviewedIds([]); setReviewed(false); setReplaceConfirmed(false); setFile(null);
    } catch (failure) {
      if (alive.current) setError((failure.name === 'AbortError' ? 'La lectura se interrumpio. No se creo ningun plato. Podes volver a intentarlo.' : failure.message) + (rows.length ? ' Tu borrador anterior sigue disponible.' : ''));
    } finally { clearTimeout(timeout); if (alive.current) setBusy(''); }
  };
  const save = async () => {
    if (saving.current || disabled || busy || (!pending && (!reviewed || !canConfirm))) return;
    let confirmation = pending;
    try {
      if (!confirmation) {
        const items = validateReviewedMenuImport(rows.filter((row) => row.selected));
        confirmation = { requestId: crypto.randomUUID(), items };
        sessionStorage.setItem(storageKey, JSON.stringify(confirmation));
        setPending(confirmation);
      }
    } catch (failure) { setError(failure.message); return; }
    saving.current = true; setBusy('saving'); setError('');
    try {
      const saved = await importReviewedMenu(menuId, confirmation.requestId, confirmation.items);
      if (!alive.current) return;
      sessionStorage.removeItem(storageKey);
      setPending(null); setResult(saved); setRows([]); setReviewed(false); setFile(null); setReviewedIds([]); setReviewIndex(0);
      onImported?.(saved);
    } catch (failure) {
      if (!alive.current) return;
      if (failure.retryable === false) {
        sessionStorage.removeItem(storageKey); setPending(null);
        if (!rows.length) setRows(confirmation.items.map((row, i) => ({ ...row, rowId: `row-${i}`, selected: true })));
        setReviewed(false); setReviewedIds([]); setReviewIndex(0);
      }
      setError(failure.message || 'No pudimos confirmar el guardado. Reintenta la misma confirmacion.');
    } finally { saving.current = false; if (alive.current) setBusy(''); }
  };
  return <section className="menu-import" aria-label="Importar carta">
    <div className="menu-import__heading">
      <div><h3>Carga tu carta desde un archivo</h3><p>Imagen o PDF. Primero revisas; despues confirmas.</p></div>
      <button type="button" onClick={() => setExpanded(!expanded)} aria-expanded={expanded}>
        {expanded ? 'Ocultar importador' : 'Importar imagen o PDF'}
      </button>
    </div>
    {expanded && <div className="menu-import__body">
      <p><strong>{companyName} / {branchName}</strong>{shared ? ' - Carta compartida: los platos tambien estaran en las otras sucursales que usen esta carta.' : ''}</p>
      <p>Se crearan platos ocultos y no disponibles para pedir. No se cargan recetas, stock, fotos individuales ni promociones. Revisa esas opciones en Editar antes de habilitarlos.</p>
      {!pending && <details className="menu-import__source" open={rows.length ? undefined : true}>
        <summary>{rows.length > 0 ? 'Leer otro archivo' : 'Seleccionar archivo de la carta'}</summary>
        <label className="menu-import__file">Archivo (PDF, JPG, PNG o WebP, hasta 8 MB)
          <input aria-label="Archivo de la carta" type="file" accept="application/pdf,image/jpeg,image/png,image/webp" disabled={Boolean(busy) || disabled}
            onChange={(event) => { setFile(event.target.files?.[0] || null); setError(''); setResult(null); setReplaceConfirmed(false); }} />
        </label>
        <p className="menu-import__hint">Al tocar Leer carta, el archivo se envia a Google Gemini para extraer el contenido. No subas datos personales. Mozzo no conserva el archivo original; revisa siempre la lectura antes de guardar. Maximo 80 platos por archivo.</p>
        {rows.length > 0 && <label className="menu-import__check"><input type="checkbox" checked={replaceConfirmed} disabled={Boolean(busy) || disabled}
          onChange={(event) => setReplaceConfirmed(event.target.checked)} /> Reemplazar este borrador solo si el nuevo archivo se lee correctamente. No modifica platos existentes.</label>}
        <button type="button" onClick={analyze} disabled={!file || Boolean(busy) || disabled || (rows.length > 0 && !replaceConfirmed)}>{busy === 'reading' ? 'Leyendo carta...' : rows.length ? 'Leer y reemplazar borrador' : 'Leer carta'}</button>
        {busy === 'reading' && <button type="button" onClick={() => request.current?.abort()}>Cancelar lectura</button>}
      </details>}
      {warnings.length > 0 && rows.length > 0 && <ul>{warnings.map((warning, i) => <li key={i}>{warning}</li>)}</ul>}
      {rows.length > 0 && !pending && <>
        {!guided && <h4>Revisa los {rows.length} platos detectados</h4>}
        <p>{draftStorageError ? 'No pudimos conservar este borrador en el navegador. No cierres esta pantalla hasta terminar.' : 'El borrador se conserva hasta 7 dias en este navegador y tu cuenta, sin guardar el archivo original. Todavia no son platos del restaurante.'}</p>
        <details className="menu-import__discard"><summary>Descartar este borrador</summary><p>Se borran solo los datos de esta lectura. Los platos del restaurante no cambian.</p>
          <button type="button" disabled={Boolean(busy) || disabled} onClick={() => { setRows([]); setWarnings([]); setReviewed(false); setReviewedIds([]); setReviewIndex(0); setReplaceConfirmed(false); setFile(null); }}>Descartar borrador</button></details>
        <p>Precios en pesos argentinos, sin separador de miles (ejemplo: 11000). Desmarca lo que no quieras importar. Los duplicados exactos no reemplazan datos existentes.</p>
        {guided ? <MenuImportReview rows={rows} index={reviewIndex} reviewedIds={reviewedIds} disabled={Boolean(busy) || disabled} duplicate={duplicate}
          onUpdate={update} onIndexChange={(index) => { setReviewIndex(index); setReviewed(false); }}
          onReview={(id) => setReviewedIds((old) => [...new Set([...old, id])])} /> :
          <div className="menu-import__rows">{rows.map((row, index) => <MenuImportRow key={row.rowId} row={row} index={index}
            disabled={Boolean(busy) || disabled} duplicate={duplicate} onUpdate={update} />)}</div>}
        {(!guided || summary) && <label className="menu-import__check"><input type="checkbox" checked={reviewed} disabled={Boolean(busy) || disabled || !canConfirm}
          onChange={(e) => setReviewed(e.target.checked)} /> Revise nombres, categorias, precios y el alcance de esta carta. Confirmo la creacion de platos ocultos.</label>}
      </>}
      {pending && <p role="status">Hay una confirmacion pendiente de {pending.items.length} platos. No cambies los datos ni inicies otra importacion: reintentar la misma confirmacion evita duplicados.</p>}
      {(pending || (rows.length > 0 && (!guided || summary))) && <button type="button" className="menu-import__confirm" disabled={Boolean(busy) || disabled || (!pending && (!reviewed || !canConfirm || !rows.some((row) => row.selected)))} onClick={save}>
        {busy === 'saving' ? 'Confirmando importacion...' : pending ? 'Reintentar confirmacion' : `Crear ${rows.filter((r) => r.selected).length} platos ocultos`}
      </button>}
      {error && <p role="alert" className="menu-import__error">{error}</p>}
      {result && <p role="status">Se crearon {result.created.length} platos ocultos. Se omitieron {result.skipped.length} duplicados sin modificarlos. Revisa los platos en Carta antes de habilitarlos.</p>}
    </div>}
  </section>;
}
